import React, { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useUnit } from 'effector-react'
import { Flex, Select, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import moment from 'moment'
import { useUrlFilters } from 'src/libs/useUrlFilters'
import {
  EMPTY_VALUE,
  FILTER_CONFIG_HISTORY,
  HISTORY_DEFAULT_ORDER,
  HISTORY_PAGE_SIZE,
  STATUS_OPTIONS,
} from '../const'
import { ActiveFilterTag, FiltersPanel, FilterSearchInput } from '../filters'
import { GatewayHistorySummary } from '../types'
import {
  $expandedGatewayIds,
  GatewayHistoryDetails,
  gatewayRowCollapsed,
  gatewayRowExpanded,
} from './details'
import { useGatewayHistory } from './hooks'
import { historyLoad } from './model'

const { Text } = Typography

export const PaymentMethodsHistory: React.FC = () => {
  const { clientID = '' } = useParams<{ clientID: string }>()
  const { filters, setFilters, resetFilters } =
    useUrlFilters(FILTER_CONFIG_HISTORY)
  const { items, total, loading } = useGatewayHistory()
  const expandedGatewayIds = useUnit($expandedGatewayIds)

  const {
    hGatewayId,
    hInternalName,
    hStatus,
    hSorterField,
    hPage,
    hPageSize,
  } = filters

  useEffect(() => {
    historyLoad({
      clientId: clientID,
      gatewayId: hGatewayId,
      internalName: hInternalName,
      status: hStatus,
      sorterField: hSorterField,
      page: hPage,
      pageSize: hPageSize,
    })
  }, [
    clientID,
    hGatewayId,
    hInternalName,
    hStatus,
    hSorterField,
    hPage,
    hPageSize,
  ])

  const setFilter = (key: string, value?: string) => {
    setFilters({ [key]: value || undefined, hPage: '1' })
  }

  const handleResetFilters = () => {
    resetFilters()
    // `resetFilters` keeps pagination keys, which would strand the user on a
    // page that no longer exists once the filters are gone.
    setFilters({ hPage: '1' })
  }

  const activeTags = useMemo<ActiveFilterTag[]>(() => {
    const tags: ActiveFilterTag[] = []

    if (hGatewayId)
      tags.push({
        key: 'hGatewayId',
        label: `Gateway ID: ${hGatewayId}`,
        clear: () => setFilter('hGatewayId'),
      })
    if (hInternalName)
      tags.push({
        key: 'hInternalName',
        label: `Internal Name: ${hInternalName}`,
        clear: () => setFilter('hInternalName'),
      })
    if (hStatus)
      tags.push({
        key: 'hStatus',
        label: `Status: ${hStatus === 'blocked' ? 'Blocked' : 'Active'}`,
        clear: () => setFilter('hStatus'),
      })

    return tags
  }, [hGatewayId, hInternalName, hStatus])

  const columns: ColumnsType<GatewayHistorySummary> = [
    {
      title: 'Gateway ID',
      dataIndex: 'gatewayId',
      width: 110,
    },
    {
      title: 'Internal Name',
      dataIndex: 'internalName',
    },
    {
      title: 'Provider',
      dataIndex: 'processingProvider',
    },
    {
      title: 'Type',
      dataIndex: 'paymentMethodType',
    },
    {
      title: 'Status',
      dataIndex: 'isBlocked',
      width: 100,
      render: (isBlocked: boolean) =>
        isBlocked ? (
          <Tag color="red" style={{ fontSize: 10, fontWeight: 700 }}>
            Blocked
          </Tag>
        ) : (
          <Tag color="success" style={{ fontSize: 10, fontWeight: 700 }}>
            Active
          </Tag>
        ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      width: 160,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      sortOrder: hSorterField === 'createdAt' ? 'ascend' : 'descend',
      render: (createdAt: string | null) =>
        createdAt ? (
          <>
            {moment.parseZone(createdAt).format('YYYY-MM-DD')}{' '}
            <Text type="secondary">
              {moment.parseZone(createdAt).format('HH:mm')}
            </Text>
          </>
        ) : (
          EMPTY_VALUE
        ),
    },
  ]

  return (
    <>
      <FiltersPanel
        rows={[
          <Flex gap={6} wrap="wrap">
            <FilterSearchInput
              placeholder="Gateway ID"
              value={hGatewayId ?? ''}
              onApply={(value) => setFilter('hGatewayId', value)}
              width={160}
              numericOnly
            />
            <FilterSearchInput
              placeholder="Internal Name"
              value={hInternalName ?? ''}
              onApply={(value) => setFilter('hInternalName', value)}
              width={260}
            />
          </Flex>,
          <Flex gap={10} wrap="wrap" align="center">
            <Select
              placeholder="Status"
              value={hStatus || undefined}
              onChange={(value) => setFilter('hStatus', value)}
              options={STATUS_OPTIONS}
              allowClear
              style={{ width: 140 }}
            />
          </Flex>,
        ]}
        tags={activeTags}
        onReset={handleResetFilters}
      />

      <Table
        dataSource={items}
        className="overflow-auto"
        loading={loading}
        rowKey="gatewayId"
        columns={columns}
        pagination={{
          current: Number(hPage ?? 1),
          pageSize: Number(hPageSize ?? HISTORY_PAGE_SIZE),
          showSizeChanger: true,
          total,
          position: ['bottomCenter'],
        }}
        rowClassName={(_, index) => `${index % 2 ? 'even-row' : 'odd-row'}`}
        expandable={{
          expandedRowKeys: expandedGatewayIds,
          onExpand: (expanded, record) =>
            expanded
              ? gatewayRowExpanded({ clientId: clientID, gatewayId: record.gatewayId })
              : gatewayRowCollapsed(record.gatewayId),
          expandedRowRender: (record) => (
            <GatewayHistoryDetails clientId={clientID} gatewayId={record.gatewayId} />
          ),
        }}
        onChange={(pagination, _, sorter: any) => {
          setFilters({
            hPage: String(pagination.current ?? 1),
            hPageSize: String(pagination.pageSize ?? HISTORY_PAGE_SIZE),
            hSorterField:
              sorter?.order === 'ascend' ? 'createdAt' : HISTORY_DEFAULT_ORDER,
          })
        }}
      />
    </>
  )
}
